import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { BewerberStatus, BewerberStatusConfig, CreateBewerberDto, Bewerber } from '../../types/bewerber';
import { StadtSelect } from '../common/StadtSelect';
import { StadtMultiSelect } from '../common/StadtMultiSelect';
import { BedarfSelect } from '../common/BedarfSelect';

interface BewerberFormProps {
  initialData?: Bewerber;
  onSubmit: (data: CreateBewerberDto) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  hideFooter?: boolean;
  formId?: string;
  onDirtyChange?: (isDirty: boolean) => void;
}

export function BewerberForm({ initialData, onSubmit, onCancel, isLoading, hideFooter, formId, onDirtyChange }: BewerberFormProps) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<CreateBewerberDto>({
    defaultValues: {
      // Personal data
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      gender: initialData?.gender || '',
      email: initialData?.email || '',
      phone: initialData?.phone || '',
      street: initialData?.street || '',
      cityId: initialData?.cityId || '',
      postalCode: initialData?.postalCode || '',
      dateOfBirth: initialData?.dateOfBirth ? initialData.dateOfBirth.split('T')[0] : '',
      // Qualification & Education
      isFachkraft: initialData?.isFachkraft ?? undefined,
      bildungsabschluss: initialData?.bildungsabschluss || '',
      ausbildungsDauer: initialData?.ausbildungsDauer || '',
      berufserfahrung: initialData?.berufserfahrung || '',
      erfahrungMitKindern: initialData?.erfahrungMitKindern ?? undefined,
      // Mobility
      hasFuehrerschein: initialData?.hasFuehrerschein ?? undefined,
      hasAuto: initialData?.hasAuto ?? undefined,
      hasOepnv: initialData?.hasOepnv ?? undefined,
      maxPendelKm: initialData?.maxPendelKm ?? undefined,
      maxPendelMinuten: initialData?.maxPendelMinuten ?? undefined,
      // Availability & Compensation
      availableFrom: initialData?.availableFrom ? initialData.availableFrom.split('T')[0] : '',
      kuendigungsfrist: initialData?.kuendigungsfrist || '',
      minWochenstunden: initialData?.minWochenstunden ?? undefined,
      maxWochenstunden: initialData?.maxWochenstunden ?? undefined,
      geforderterStundensatz: initialData?.geforderterStundensatz ?? undefined,
      angebotenerStundensatz: initialData?.angebotenerStundensatz ?? undefined,
      tvoedsueLevel: initialData?.tvoedsueLevel || '',
      tvoedsueStufe: initialData?.tvoedsueStufe ?? undefined,
      // Interview assessment (Fazit)
      geeignetFuer: initialData?.geeignetFuer ?? [],
      // Other
      ortStelleIds: initialData?.ortStelleIds ?? [],
      beworbenAufBedarfId: initialData?.beworbenAufBedarfId ?? null,
      status: initialData?.status || BewerberStatus.NEU,
      notes: initialData?.notes || '',
    },
  });

  // Notify parent of dirty state changes
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Safari has a bug where typing quickly in the year field can corrupt the value
  // e.g., typing "1963" as the year results in "0063" instead
  // This handler detects corrupted years (< 100) and auto-corrects them
  const handleDateOfBirthBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value) return;

    const [year, month, day] = value.split('-').map(Number);

    // Only fix if year appears corrupted (< 100)
    if (year >= 100) return;

    const today = new Date();
    const twoDigitYear = year % 100;

    // Try 2000s first
    let correctedYear = 2000 + twoDigitYear;

    // If that would be in the future, use 1900s (birth dates can't be in future)
    const candidateDate = new Date(correctedYear, month - 1, day);
    if (candidateDate > today) {
      correctedYear = 1900 + twoDigitYear;
    }

    const correctedValue = `${correctedYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

    // Update both the DOM and react-hook-form state
    e.target.value = correctedValue;
    setValue('dateOfBirth', correctedValue);
  };

  const handleFormSubmit = async (data: CreateBewerberDto) => {
    // Clean up empty strings to undefined
    const cleanedData: CreateBewerberDto = {
      // Personal data
      firstName: data.firstName,
      lastName: data.lastName,
      gender: data.gender || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      street: data.street || undefined,
      cityId: data.cityId || undefined,
      postalCode: data.postalCode || undefined,
      dateOfBirth: data.dateOfBirth || undefined,
      // Qualification & Education
      isFachkraft: data.isFachkraft,
      bildungsabschluss: data.bildungsabschluss || undefined,
      ausbildungsDauer: data.ausbildungsDauer || undefined,
      berufserfahrung: data.berufserfahrung || undefined,
      erfahrungMitKindern: data.erfahrungMitKindern,
      // Mobility
      hasFuehrerschein: data.hasFuehrerschein,
      hasAuto: data.hasAuto,
      hasOepnv: data.hasOepnv,
      maxPendelKm: data.maxPendelKm || undefined,
      maxPendelMinuten: data.maxPendelMinuten || undefined,
      // Availability & Compensation
      availableFrom: data.availableFrom || undefined,
      kuendigungsfrist: data.kuendigungsfrist || undefined,
      minWochenstunden: data.minWochenstunden || undefined,
      maxWochenstunden: data.maxWochenstunden || undefined,
      geforderterStundensatz: data.geforderterStundensatz || undefined,
      angebotenerStundensatz: data.angebotenerStundensatz || undefined,
      tvoedsueLevel: data.tvoedsueLevel || undefined,
      tvoedsueStufe: data.tvoedsueStufe || undefined,
      // Interview assessment (Fazit)
      geeignetFuer: data.geeignetFuer ?? [],
      // Other
      ortStelleIds: data.ortStelleIds ?? [],
      beworbenAufBedarfId: data.beworbenAufBedarfId ?? null,
      status: data.status,
      notes: data.notes || undefined,
    };
    await onSubmit(cleanedData);
  };

  const inputClassName = "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm";
  const labelClassName = "block text-xs font-medium text-gray-700";
  const checkboxClassName = "h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500";

  return (
    <form id={formId} onSubmit={handleSubmit(handleFormSubmit)} className={hideFooter ? '' : 'pb-16'}>
      {/* Grid container for sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {/* Section 1: Personal Data */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Persönliche Daten</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <label htmlFor="firstName" className={labelClassName}>Vorname *</label>
                <input
                  type="text"
                  id="firstName"
                  {...register('firstName', { required: 'Vorname ist erforderlich' })}
                  className={inputClassName}
                />
                {errors.firstName && (
                  <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className={labelClassName}>Nachname *</label>
                <input
                  type="text"
                  id="lastName"
                  {...register('lastName', { required: 'Nachname ist erforderlich' })}
                  className={inputClassName}
                />
                {errors.lastName && (
                  <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="gender" className={labelClassName}>Geschlecht</label>
                <select id="gender" {...register('gender')} className={inputClassName}>
                  <option value="">-- Bitte wählen --</option>
                  <option value="MAENNLICH">Männlich</option>
                  <option value="WEIBLICH">Weiblich</option>
                  <option value="DIVERS">Divers</option>
                </select>
              </div>

              <div>
                <label htmlFor="dateOfBirth" className={labelClassName}>Geburtsdatum</label>
                <input
                  type="date"
                  id="dateOfBirth"
                  {...register('dateOfBirth')}
                  onBlur={handleDateOfBirthBlur}
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="email" className={labelClassName}>E-Mail</label>
                <input
                  type="email"
                  id="email"
                  {...register('email', {
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: 'Ungültige E-Mail-Adresse',
                    },
                  })}
                  className={inputClassName}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className={labelClassName}>Telefon</label>
                <input
                  type="tel"
                  id="phone"
                  {...register('phone')}
                  placeholder="+49 171 1234567"
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="street" className={labelClassName}>Straße</label>
                <input
                  type="text"
                  id="street"
                  {...register('street')}
                  placeholder="Musterstraße 123"
                  className={inputClassName}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label htmlFor="postalCode" className={labelClassName}>PLZ</label>
                  <input
                    type="text"
                    id="postalCode"
                    {...register('postalCode')}
                    placeholder="50667"
                    className={inputClassName}
                  />
                </div>
                <div className="col-span-2">
                  <StadtSelect
                    id="cityId"
                    label="Stadt (Wohnort)"
                    value={watch('cityId') || ''}
                    onChange={(id) => setValue('cityId', id)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 2: Qualification & Mobility */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Qualifikation & Mobilität</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <label className={labelClassName}>Fachkraft</label>
                <div className="mt-1 flex items-center space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="isFachkraft"
                      checked={watch('isFachkraft') === true}
                      onChange={() => setValue('isFachkraft', true)}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Ja</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      name="isFachkraft"
                      checked={watch('isFachkraft') === false}
                      onChange={() => setValue('isFachkraft', false)}
                      className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Nein</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="bildungsabschluss" className={labelClassName}>Bildungsabschluss</label>
                <input
                  type="text"
                  id="bildungsabschluss"
                  {...register('bildungsabschluss')}
                  placeholder="z.B. Bachelor Pädagogik"
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="ausbildungsDauer" className={labelClassName}>Ausbildungsdauer</label>
                <input
                  type="text"
                  id="ausbildungsDauer"
                  {...register('ausbildungsDauer')}
                  placeholder="z.B. 3 Jahre"
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="berufserfahrung" className={labelClassName}>Berufserfahrung</label>
                <textarea
                  id="berufserfahrung"
                  rows={2}
                  {...register('berufserfahrung')}
                  placeholder="Relevante Berufserfahrung..."
                  className={inputClassName}
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="erfahrungMitKindern"
                  {...register('erfahrungMitKindern')}
                  className={checkboxClassName}
                />
                <label htmlFor="erfahrungMitKindern" className="ml-2 text-sm text-gray-700">
                  Erfahrung mit Kindern
                </label>
              </div>

              {/* Mobility subheading */}
              <div className="pt-2">
                <span className="text-sm font-medium text-gray-700">Mobilität</span>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasFuehrerschein"
                  {...register('hasFuehrerschein')}
                  className={checkboxClassName}
                />
                <label htmlFor="hasFuehrerschein" className="ml-2 text-sm text-gray-700">
                  Führerschein
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasAuto"
                  {...register('hasAuto')}
                  className={checkboxClassName}
                />
                <label htmlFor="hasAuto" className="ml-2 text-sm text-gray-700">
                  Auto vorhanden
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="hasOepnv"
                  {...register('hasOepnv')}
                  className={checkboxClassName}
                />
                <label htmlFor="hasOepnv" className="ml-2 text-sm text-gray-700">
                  ÖPNV
                </label>
              </div>

              <div>
                <label htmlFor="maxPendelKm" className={labelClassName}>Max. Pendelstrecke (km)</label>
                <input
                  type="number"
                  id="maxPendelKm"
                  {...register('maxPendelKm', { valueAsNumber: true, min: 0 })}
                  placeholder="z.B. 25"
                  min="0"
                  className={inputClassName}
                />
              </div>

              <div>
                <label htmlFor="maxPendelMinuten" className={labelClassName}>Max. Pendelzeit (Min.)</label>
                <input
                  type="number"
                  id="maxPendelMinuten"
                  {...register('maxPendelMinuten', { valueAsNumber: true, min: 0 })}
                  placeholder="z.B. 30"
                  min="0"
                  className={inputClassName}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Availability & Compensation */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Verfügbarkeit & Vergütung</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <label htmlFor="availableFrom" className={labelClassName}>Verfügbar ab</label>
                <input type="date" id="availableFrom" {...register('availableFrom')} className={inputClassName} />
              </div>

              <div>
                <label htmlFor="kuendigungsfrist" className={labelClassName}>Kündigungsfrist</label>
                <input
                  type="text"
                  id="kuendigungsfrist"
                  {...register('kuendigungsfrist')}
                  placeholder="z.B. 4 Wochen zum Monatsende"
                  className={inputClassName}
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="minWochenstunden" className={labelClassName}>Min. Std./Woche</label>
                  <input
                    type="number"
                    id="minWochenstunden"
                    {...register('minWochenstunden', { valueAsNumber: true, min: 0, max: 50 })}
                    placeholder="10"
                    min="0"
                    max="50"
                    className={inputClassName}
                  />
                </div>
                <div>
                  <label htmlFor="maxWochenstunden" className={labelClassName}>Max. Std./Woche</label>
                  <input
                    type="number"
                    id="maxWochenstunden"
                    {...register('maxWochenstunden', { valueAsNumber: true, min: 0, max: 50 })}
                    placeholder="30"
                    min="0"
                    max="50"
                    className={inputClassName}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="geforderterStundensatz" className={labelClassName}>Gefordert (€/Std)</label>
                  <input
                    type="number"
                    id="geforderterStundensatz"
                    step="0.01"
                    {...register('geforderterStundensatz', { valueAsNumber: true, min: 0 })}
                    placeholder="18.50"
                    min="0"
                    className={inputClassName}
                  />
                </div>
                <div>
                  <label htmlFor="angebotenerStundensatz" className={labelClassName}>Angeboten (€/Std)</label>
                  <input
                    type="number"
                    id="angebotenerStundensatz"
                    step="0.01"
                    {...register('angebotenerStundensatz', { valueAsNumber: true, min: 0 })}
                    placeholder="17.00"
                    min="0"
                    className={inputClassName}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="tvoedsueLevel" className={labelClassName}>TVöD SUE</label>
                  <select id="tvoedsueLevel" {...register('tvoedsueLevel')} className={inputClassName}>
                    <option value="">--</option>
                    <option value="S2">S2</option>
                    <option value="S3">S3</option>
                    <option value="S4">S4</option>
                    <option value="S5">S5</option>
                    <option value="S6">S6</option>
                    <option value="S7">S7</option>
                    <option value="S8a">S8a</option>
                    <option value="S8b">S8b</option>
                    <option value="S9">S9</option>
                    <option value="S10">S10</option>
                    <option value="S11a">S11a</option>
                    <option value="S11b">S11b</option>
                    <option value="S12">S12</option>
                    <option value="S13">S13</option>
                    <option value="S14">S14</option>
                    <option value="S15">S15</option>
                    <option value="S16">S16</option>
                    <option value="S17">S17</option>
                    <option value="S18">S18</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="tvoedsueStufe" className={labelClassName}>Erfahrungsstufe</label>
                  <select id="tvoedsueStufe" {...register('tvoedsueStufe', { valueAsNumber: true })} className={inputClassName}>
                    <option value="">--</option>
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                    <option value={6}>6</option>
                  </select>
                </div>
              </div>

              <div>
                <span className={labelClassName}>Geeignet für (Fazit)</span>
                <div className="mt-1 flex flex-wrap gap-4">
                  {[
                    { value: 'koerperlich', label: 'Körperlich' },
                    { value: 'seelisch', label: 'Seelisch' },
                    { value: 'pflegerisch', label: 'Pflegerisch' },
                  ].map((option) => {
                    const selected = watch('geeignetFuer') ?? [];
                    return (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selected.includes(option.value)}
                          onChange={(e) =>
                            setValue(
                              'geeignetFuer',
                              e.target.checked
                                ? [...selected, option.value]
                                : selected.filter((v) => v !== option.value),
                              { shouldDirty: true },
                            )
                          }
                          className={checkboxClassName}
                        />
                        <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Other */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Sonstiges</h3>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              <div>
                <StadtMultiSelect
                  id="ortStelleIds"
                  label="Ort der Stelle (mehrere möglich)"
                  value={watch('ortStelleIds') ?? []}
                  onChange={(ids) => setValue('ortStelleIds', ids, { shouldDirty: true })}
                />
              </div>

              <div>
                <BedarfSelect
                  id="beworbenAufBedarfId"
                  label="Stelle"
                  value={watch('beworbenAufBedarfId') ?? null}
                  onChange={(id, bedarf) => {
                    setValue('beworbenAufBedarfId', id, { shouldDirty: true });
                    // A Stelle names its city implicitly. Filling it in right
                    // here rather than on the server keeps the chip visible and
                    // correctable before saving. Only ever when nothing is set —
                    // a city chosen on purpose is never overwritten.
                    const cities = watch('ortStelleIds') ?? [];
                    if (bedarf?.schoolCityId && cities.length === 0) {
                      setValue('ortStelleIds', [bedarf.schoolCityId], { shouldDirty: true });
                    }
                  }}
                />
              </div>

              <div>
                <label htmlFor="status" className={labelClassName}>Status</label>
                <select id="status" {...register('status')} className={inputClassName}>
                  {Object.entries(BewerberStatusConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6: Notes - spans 2 columns */}
        <div className="bg-white shadow rounded-lg md:col-span-2">
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="text-base font-medium text-gray-900">Notizen</h3>
          </div>
          <div className="p-4">
            <textarea
              id="notes"
              rows={6}
              {...register('notes')}
              placeholder="Zusätzliche Informationen, Bemerkungen, Notizen..."
              className={inputClassName}
            />
          </div>
        </div>
      </div>

      {/* Sticky Footer Actions */}
      {!hideFooter && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 shadow-lg z-10">
          <div className="max-w-7xl mx-auto flex justify-end space-x-3">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
